import customersService from '@/services/customers';
import salesService from '@/services/sales';
import generalCatalogsService from '@/services/generalCatalogs';
import { formatRelativeDaysFromNow } from '@/utils/datePresentation';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { normalizeGeoOptions as normalizeGeoOptionsUtil } from '@/utils/catalogOptions';
import { buildCreateSaleRequestData } from '@/composables/sales/useSalesCreateModal';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export const customerDetailGeneralMethods = {
  formatRelativeDate(value) {
    return formatRelativeDaysFromNow(value);
  },
  async ensureGeoCatalogsLoaded() {
    if (this.countryOptions.length > 0 || this.stateOptions.length > 0) {
      return;
    }

    const [countriesResult, statesResult] = await Promise.allSettled([
      generalCatalogsService.getCountries(),
      generalCatalogsService.getStates(),
    ]);

    if (countriesResult.status === 'fulfilled') {
      this.countryOptions = normalizeGeoOptionsUtil(extractCollection(countriesResult.value));
    }

    if (statesResult.status === 'fulfilled') {
      this.stateOptions = normalizeGeoOptionsUtil(extractCollection(statesResult.value));
    }
  },
  openCustomerEditModal(sectionKey = 'general') {
    this.customerEditSectionKey = String(sectionKey || 'general');
    this.isCustomerEditModalOpen = true;
  },
  closeCustomerEditModal() {
    this.isCustomerEditModalOpen = false;
    this.customerEditSectionKey = 'general';
  },
  openCreateSaleModal(kind = 'business') {
    const normalizedKind = String(kind || 'business').trim().toLowerCase();
    const isFlatRate = normalizedKind === 'flat-rate';

    this.saleModalMode = 'create';
    this.saleModalKind = isFlatRate ? 'flat-rate' : 'business';
    this.saleModalServiceFilterMode = isFlatRate ? 'flat-rate' : 'standard';
    this.saleModalInitialData = {
      customer_id: Number(this.customer?.id || this.customerId || 0),
      company_id: Number(this.customer?.id || this.customerId || 0),
      company_name: String(this.customer?.name || '').trim(),
      customer_name: String(this.customer?.name || '').trim(),
    };
    this.showCreateSaleModal = true;
  },
  closeCreateSaleModal(force = false) {
    if (!force && this.createSaleSubmitting) {
      return;
    }

    this.showCreateSaleModal = false;

    if (force) {
      this.saleModalMode = 'create';
      this.saleModalKind = 'business';
      this.saleModalServiceFilterMode = 'standard';
      this.saleModalInitialData = null;
    }
  },
  async submitCreateSale(payload) {
    this.createSaleSubmitting = true;

    try {
      const isFlatRate = String(this.saleModalKind || '').trim().toLowerCase() === 'flat-rate';
      const requestData = buildCreateSaleRequestData(payload);
      const response = isFlatRate
        ? await salesService.flatRates(requestData)
        : await salesService.store(requestData);
      const createdSale = extractPayload(response);
      const createdSaleId = Number(createdSale?.id || createdSale?.sale_id || 0);

      const fallbackError = isFlatRate
        ? text.detail.generalMethods.retainerCreateError
        : text.detail.generalMethods.businessCreateError;
      const fallbackSuccess = isFlatRate
        ? text.detail.generalMethods.retainerCreateSuccess
        : text.detail.generalMethods.businessCreateSuccess;
      const apiErrorMessage = extractApiErrorMessage(response, fallbackError);

      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, fallbackSuccess));
      this.closeCreateSaleModal(true);

      if (createdSaleId > 0) {
        this.$router.push({ name: 'saleDetail', params: { saleId: createdSaleId } }).catch(() => {});
        return;
      }

      await this.fetchCustomerDetail();
    } catch (error) {
      const isFlatRate = String(this.saleModalKind || '').trim().toLowerCase() === 'flat-rate';
      const message = error?.response?.data?.message || (
        isFlatRate
          ? text.detail.generalMethods.retainerCreateError
          : text.detail.generalMethods.businessCreateError
      );
      this.$toast?.error(message);
    } finally {
      this.createSaleSubmitting = false;
    }
  },
  handleDeleteCustomerFromModal(customer) {
    if (!customer?.id) {
      return;
    }

    this.closeCustomerEditModal();
    this.pendingActionType = 'delete-customer';
    this.pendingActionCustomer = { ...customer };
    this.pendingActionPerson = null;
    this.pendingActionContributor = null;
    this.isConfirmActionModalOpen = true;
  },
  handleDeleteCustomerFromAdvancedOptions() {
    const customerId = Number(this.customer?.id || this.customerId || 0);
    if (customerId <= 0) {
      return;
    }

    this.pendingActionType = 'delete-customer';
    this.pendingActionCustomer = { ...this.customer };
    this.pendingActionPerson = null;
    this.pendingActionContributor = null;
    this.isConfirmActionModalOpen = true;
  },
  handleOpenCustomerPermissions() {
    this.openCustomerVisibilityModal();
  },
  handleOpenCustomerMerge() {
    this.openCustomerMergeModal();
  },
  openCustomerMergeModal() {
    const customerId = Number(this.customer?.id || this.customerId || 0);
    if (customerId <= 0) {
      return;
    }

    this.isMergeModalOpen = true;
  },
  closeCustomerMergeModal() {
    this.isMergeModalOpen = false;
  },
  async submitCustomerMerge(payload) {
    const customerId = Number(this.customer?.id || this.customerId || 0);
    if (customerId <= 0) {
      return;
    }

    this.mergeSubmitting = true;

    try {
      const response = await customersService.merge(customerId, payload || {});
      const apiErrorMessage = extractApiErrorMessage(response, text.detail.generalMethods.customerMergeError);

      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, text.detail.generalMethods.customerMergeSuccess));
      this.closeCustomerMergeModal();
      await this.fetchCustomerDetail();
    } catch (error) {
      const message = error?.response?.data?.message || text.detail.generalMethods.customerMergeError;
      this.$toast?.error(message);
    } finally {
      this.mergeSubmitting = false;
    }
  },
  handleArchiveCustomerToggle() {
    const customerId = Number(this.customer?.id || this.customerId || 0);
    if (customerId <= 0) {
      return;
    }

    const isActive = Number(this.customer?.active ?? 0) === 1;

    this.pendingActionType = isActive ? 'archive-customer' : 'unarchive-customer';
    this.pendingActionCustomer = { ...this.customer };
    this.pendingActionPerson = null;
    this.pendingActionContributor = null;
    this.isConfirmActionModalOpen = true;
  },
  onHeaderAction(action) {
    const actionKey = String(action?.key || '');

    if (actionKey === 'edit-customer') {
      this.openCustomerEditModal('general');
      return;
    }

    if (actionKey === 'new-business') {
      this.openCreateSaleModal('business');
      return;
    }

    if (actionKey === 'new-retainer') {
      this.openCreateSaleModal('flat-rate');
      return;
    }

    if (actionKey === 'invoice-customer') {
      const defaultBillingDataId = Number(this.defaultBillingData?.id || 0);
      if (defaultBillingDataId <= 0) {
        this.$toast?.warning(text.detail.generalMethods.invoiceBillingDataRequired);
        return;
      }

      this.$router.push({
        name: 'invoiceCreate',
        query: {
          customer_id: String(this.customerId),
          contributor_id: String(defaultBillingDataId),
        },
      }).catch(() => {});
    }
  },
  handleViewSaleDetail(sale) {
    const saleId = Number(sale?.id || sale?.sale_id || 0);
    if (saleId <= 0) {
      this.$toast?.info(text.detail.generalMethods.saleDetailPending);
      return;
    }

    this.$router.push({ name: 'saleDetail', params: { saleId } }).catch(() => {});
  },
  handleChangeSaleStatus(sale) {
    const saleName = String(sale?.sale_name || sale?.name || sale?.code || '').trim();
    if (saleName !== '') {
      this.$toast?.info(text.detail.generalMethods.saleStatusChangeByName(saleName));
      return;
    }

    this.$toast?.info(text.detail.generalMethods.saleStatusChangePending);
  },
  async submitCustomerEdit(payload) {
    try {
      await customersService.update(this.customerId, payload || {});
      this.$toast?.success(text.detail.generalMethods.customerUpdateSuccess);
      this.closeCustomerEditModal();
      await this.fetchCustomerDetail();
    } catch (error) {
      const message = error?.response?.data?.message || text.detail.generalMethods.customerUpdateError;
      this.$toast?.error(message);
    }
  },
  async openCustomerVisibilityModal() {
    const customerId = Number(this.customer?.id || this.customerId || 0);
    if (customerId <= 0) {
      return;
    }

    this.isVisibilityModalOpen = true;

    if (this.visibilityUsersOptions.length > 0 || this.visibilityUsersLoading) {
      return;
    }

    this.visibilityUsersLoading = true;
    try {
      const response = await generalCatalogsService.getUsers();
      this.visibilityUsersOptions = extractCollection(response);
    } catch (error) {
      this.visibilityUsersOptions = [];
    } finally {
      this.visibilityUsersLoading = false;
    }
  },
  closeCustomerVisibilityModal() {
    if (this.visibilitySubmitting) {
      return;
    }

    this.isVisibilityModalOpen = false;
  },
  async submitCustomerVisibility(payload) {
    const customerId = Number(this.customer?.id || this.customerId || 0);
    if (customerId <= 0) {
      return;
    }

    this.visibilitySubmitting = true;
    try {
      const response = await customersService.updateVisibility(customerId, payload || {});
      const visibilitySettings = extractPayload(response);

      this.customer = {
        ...(this.customer || {}),
        visibility: String(visibilitySettings?.mode || this.customer?.visibility || 'public').trim() || 'public',
        visibility_settings: {
          mode: String(visibilitySettings?.mode || 'public').trim() || 'public',
          private_user_ids: Array.isArray(visibilitySettings?.private_user_ids) ? visibilitySettings.private_user_ids : [],
          hidden_user_ids: Array.isArray(visibilitySettings?.hidden_user_ids) ? visibilitySettings.hidden_user_ids : [],
          selected_user_ids: Array.isArray(visibilitySettings?.selected_user_ids) ? visibilitySettings.selected_user_ids : [],
        },
      };

      this.$toast?.success(text.detail.generalMethods.customerVisibilityUpdateSuccess);
      this.closeCustomerVisibilityModal();
    } catch (error) {
      const message = error?.response?.data?.message || text.detail.generalMethods.customerVisibilityUpdateError;
      this.$toast?.error(message);
    } finally {
      this.visibilitySubmitting = false;
    }
  },
  async fetchCustomerDetail() {
    if (!this.customerId) {
      this.$toast?.error(text.detail.generalMethods.invalidCustomer);
      this.$router.push({ name: 'customers' });
      return;
    }

    this.loading = true;
    try {
      const response = await customersService.show(this.customerId);
      this.customer = extractPayload(response);

      await Promise.all([
        this.fetchCustomerMetrics(),
        this.fetchCustomerOverview(),
        this.fetchPersons(),
        this.fetchContributors(),
      ]);
    } catch (error) {
      const message = error?.response?.data?.message || text.detail.generalMethods.loadDetailError;
      this.$toast?.error(message);
      this.$router.push({ name: 'customers' });
    } finally {
      this.loading = false;
    }
  },
  async fetchCustomerMetrics() {
    if (!this.customerId) {
      this.customerMetrics = null;
      return;
    }

    try {
      const response = await customersService.detailMetrics(this.customerId);
      this.customerMetrics = extractPayload(response);
    } catch (error) {
      this.customerMetrics = null;
    }
  },
  async fetchCustomerOverview() {
    if (!this.customerId) {
      this.customerOverview = null;
      return;
    }

    try {
      const response = await customersService.detailOverview(this.customerId);
      this.customerOverview = extractPayload(response);
    } catch (error) {
      this.customerOverview = null;
    }
  },
};